import React, { useRef, useState } from 'react'
import { startOfToday, subDays, format } from 'date-fns'
import { RangeFocus } from 'react-date-range'

import { useAppDispatch, useAppSelector } from 'redux/Hook'
import { setPage, setUserFilters } from 'redux/slices/TextAnalysis'
import DateRangeSelector from './DateRangeSelector'
import CustomDateRangePopover from './CustomDateRangePopover'
import {
  formatSelectedDateRange,
  DEFAULT_DATERANGE_FORMAT,
  getDefaultDateRange,
  type Range,
  type DateRangeOption,
} from './Helper'

const DateRangeFilter: React.FC = () => {
  const dispatch = useAppDispatch()

  const {
    allContributions: { fetching },
  } = useAppSelector((state) => state.textAnalysis)

  const textFieldRef = useRef<null | HTMLInputElement>(null)
  const [customDateRangeAnchorElement, setCustomDateRangeAnchorElement] =
    useState<null | HTMLElement>(null)
  const [focusedRange, setFocusedRange] = useState<RangeFocus>([0, 0])

  const [selectedDateRange, setSelectedDateRange] = useState({
    startDate: subDays(startOfToday(), 7),
    endDate: startOfToday(),
  })
  const [selectedDateRangeAsString, setSelectedDateRangeAsString] = useState(
    formatSelectedDateRange(selectedDateRange),
  )

  const handleSelectedDateRange = (selection: Range) => {
    dispatch(setPage(1))
    setSelectedDateRangeAsString(formatSelectedDateRange(selection))

    dispatch(
      setUserFilters({
        created_at: {
          start_date: format(selection.startDate, DEFAULT_DATERANGE_FORMAT),
          end_date: format(selection.endDate, DEFAULT_DATERANGE_FORMAT),
        },
      }),
    )
  }

  const handleRangeOptionSelect = (selectedRangeOption: DateRangeOption) => {
    if (selectedRangeOption === 'custom') {
      setCustomDateRangeAnchorElement(
        customDateRangeAnchorElement ? null : textFieldRef.current,
      )
      return
    }

    setCustomDateRangeAnchorElement(null)

    const newDateRange = getDefaultDateRange(selectedRangeOption)

    setSelectedDateRange(newDateRange)
    handleSelectedDateRange(newDateRange)
  }

  const handleCustomDateRangeChange = ({ selection }: any) => {
    setSelectedDateRange(selection)

    if (focusedRange[1] === 1) {
      handleSelectedDateRange(selection)
      setCustomDateRangeAnchorElement(null)
    }
  }

  return (
    <>
      <DateRangeSelector
        selectedDateRangeAsString={selectedDateRangeAsString}
        onRangeOptionSelect={handleRangeOptionSelect}
        disabled={fetching}
        ref={textFieldRef}
      />
      <CustomDateRangePopover
        open={Boolean(customDateRangeAnchorElement)}
        anchorEl={customDateRangeAnchorElement}
        onClose={() => setCustomDateRangeAnchorElement(null)}
        selectedDateRange={selectedDateRange}
        onDateRangeChange={handleCustomDateRangeChange}
        focusedRange={focusedRange}
        onDateRangeFocusChange={setFocusedRange}
      />
    </>
  )
}

export default DateRangeFilter
